import React, { FunctionComponent, useEffect, useState } from "react";
import { Box, Card, DialogContent, Divider, Stack, Toolbar, useTheme } from "@mui/material";
import { strings } from "../../../localization/LocalizedStrings";
import { AlertDialog } from "../../AlertDialog";
import styles from "./styles.module.scss";
import { FilterElement, FilterSortOption, FilterSortOrder } from "../../../models/FilterElement";
import { ToolbarButton } from "../../toolbar/ToolbarButton";
import { SearchBar } from "../../SearchBar";
import { PiperDialog } from "../../PiperDialog";
import { IconType } from "../../../icons";
import {
  filterIsNegatedInit,
  IAlbum,
  IIsNegatedAlbumsParameters,
  useSearchAlbums
} from "../../../hooks/albums";
import { PageSizeHelper } from "../../../models/PageSizeHelper";
import { UserRole } from "../../../hooks/users";
import { useUser } from "../../../hooks/session";
import { AlbumList } from "../AlbumList";
import { AlbumsFilterDialog } from "../AlbumsFilter";
import { useAlbumFilterManager } from "../../../hooks/albums/useAlbumFilterManager";
import { ToolbarBox } from "../../toolbar/ToolbarBox";
import { ToolbarRawButton } from "../../toolbar/ToolbarRawButton";

export const AlbumsDesktop: FunctionComponent<IAlbumsDesktopProps> = ({
  open,
  onClose,
  onAlbumSelected
}) => {
  const theme = useTheme();
  const [currentUser] = useUser.useState();
  const [loading, setLoading] = useState(true);
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [filterElements, setFilterElements] = useState<FilterElement[]>([]);
  const [isNegated, setIsNegated] = useState<IIsNegatedAlbumsParameters>(filterIsNegatedInit());
  const [sortOption, setSortOption] = useState(FilterSortOption.ARTIST);
  const [sortOrder, setSortOrder] = useState(FilterSortOrder.ASCENDANT);
  const [isAlbumsFilterDialogOpen, setIsAlbumsFilterDialogOpen] = useState(false);
  const [needMoreAlbums, setNeedMoreAlbums] = useState(false);
  const { getSearchText, getFilterElementsWithSearchText } = useAlbumFilterManager(false);
  const onError = (body: any) => {
    setAlertMessage(body.message);
    setIsAlertOpen(true);
  };
  const isFilterUnapplied = () => {
    return (
      (!filterElements || filterElements.length == 0) &&
      sortOption == FilterSortOption.ARTIST &&
      sortOrder == FilterSortOrder.ASCENDANT
    );
  };
  const [albums, , resetAlbumStorage, , , noMoreAlbums, isFetchingAlbums] = useSearchAlbums(
    open,
    "desktop",
    !isFilterUnapplied(),
    isNegated,
    PageSizeHelper.albums(currentUser?.role ?? UserRole.common),
    setLoading,
    onError,
    undefined,
    filterElements,
    sortOption,
    sortOrder,
    [needMoreAlbums]
  );
  useEffect(() => {
    if (open) {
      resetAlbumStorage();
      setNeedMoreAlbums(state => !state);
    }
  }, [open]);
  const searchBar = () => (
    <Stack direction={"row"} alignItems={"center"}>
      <SearchBar
        className={styles.searchBar}
        forceThisText={getSearchText(filterElements)}
        onSearchRequested={searchText => {
          setFilterElements(getFilterElementsWithSearchText(searchText, filterElements));
          resetAlbumStorage();
          setNeedMoreAlbums(state => !state);
        }}
        onSearchDismissed={() => {
          setFilterElements(getFilterElementsWithSearchText("", filterElements));
          resetAlbumStorage();
          setNeedMoreAlbums(state => !state);
        }}
        timed
      />
    </Stack>
  );
  return (
    <PiperDialog
      fullWidth
      maxWidth={"xs"}
      open={open}
      onClose={onClose}
      PaperProps={{ style: { height: "100%" } }}
    >
      <Toolbar style={{ minHeight: "unset" }}>
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <Stack direction="row" alignItems={"center"}>
            <h3 style={{ marginRight: 8 }}>{strings.albums_plural_title}</h3>
            <ToolbarBox className={styles.toolbarBox}>
              <ToolbarRawButton
                onClick={() => setIsAlbumsFilterDialogOpen(true)}
                tooltip={strings.option_filter_albums}
                iconType={IconType.FilterAlt}
                highlighted={!isFilterUnapplied()}
                disabled={isFetchingAlbums}
              />
              <Divider orientation="vertical" flexItem />
              <ToolbarRawButton
                onClick={() => {
                  setSortOption(FilterSortOption.ARTIST);
                  setSortOrder(FilterSortOrder.ASCENDANT);
                  setFilterElements([]);
                  resetAlbumStorage();
                  setNeedMoreAlbums(state => !state);
                }}
                disabled={isFilterUnapplied() || loading}
                tooltip={strings.clear_filter}
                iconType={IconType.FilterAltOff}
                color={theme.palette.colors?.remove}
              />
            </ToolbarBox>
            <Box flexGrow={1} />
            <ToolbarButton
              loading={loading || isFetchingAlbums}
              onClick={onClose}
              tooltip={strings.close}
              iconType={IconType.Close}
            />
          </Stack>
          {searchBar()}
        </div>
      </Toolbar>
      <Card className={styles.contentCard}>
        <DialogContent
          sx={{
            padding: 0,
            display: "flex",
            flexDirection: "column"
          }}
        >
          <AlbumList
            albums={albums}
            dragDropId={""}
            isPublic={false}
            sortOption={sortOption}
            canMoveAlbums={false}
            canDropAlbums={false}
            canLoadMoreAlbums={true}
            noMoreAlbums={noMoreAlbums}
            setNeedMoreAlbums={() => setNeedMoreAlbums(state => !state)}
            onAlbumSelected={album => {
              onAlbumSelected(album);
              onClose();
            }}
            scrollToTopTrigger={false}
            isForLayout={false}
          />
        </DialogContent>
      </Card>
      <AlbumsFilterDialog
        isPublic={false}
        isOpen={isAlbumsFilterDialogOpen}
        setIsOpen={setIsAlbumsFilterDialogOpen}
        initialFilterElements={filterElements}
        initialIsNegated={isNegated}
        initialSortOption={sortOption}
        initialSortOrder={sortOrder}
        onFilterApplied={(filterElements, isNegated, sortOption, sortOrder) => {
          setIsNegated(isNegated);
          setSortOption(sortOption);
          setSortOrder(sortOrder);
          setFilterElements(filterElements);
          resetAlbumStorage();
          setNeedMoreAlbums(state => !state);
        }}
      />
      <AlertDialog message={alertMessage} open={isAlertOpen} setOpen={setIsAlertOpen} />
    </PiperDialog>
  );
};

export interface IAlbumsDesktopProps {
  open: boolean;
  onClose: () => void;
  onAlbumSelected?: (album: IAlbum) => void;
}
